/**
 * The export collector (Rechnungen 2.0 — R8).
 *
 * THE EXPORT IS THE POPULATION, NOT THE PAGE. The table shows 25 rows of a
 * filtered set and the four cards above it describe the whole of it; a file that
 * contained only the visible page would be the R0 truncation defect wearing a
 * different hat. So this collector takes the source, the view, the filters and
 * the sort exactly as they are on screen, and then ignores the two parameters
 * that describe a PAGE: it always starts at offset zero and always asks for
 * `EXPORT_PAGE_SIZE`, which is the maximum the endpoint contract allows.
 *
 * IT STOPS AT THE FIRST ANSWER'S `total`, DELIBERATELY. The obvious alternative
 * — keep asking while pages come back full — follows rows that are created while
 * the export runs. In a busy organisation it need never terminate, and what it
 * produces is neither the population at the start nor the one at the end.
 * Freezing `initialTotal` from the first response gives a defined stopping
 * point. This is NOT a database snapshot: rows added mid-run are not in the
 * file, and rows deleted mid-run may shift the later pages. That limitation is
 * written down here rather than papered over with a heuristic that hides it.
 *
 * A PARTIAL FILE IS WORSE THAN NO FILE. A CSV missing page three looks complete
 * — it has a header, it opens, it has data, and nothing in it says which rows
 * are absent. So any page that fails, or that answers for a different source, or
 * that reports a payment availability other than `available`, aborts the whole
 * run: the error is shown and nothing is downloaded.
 *
 * NOTHING IS SORTED, FILTERED OR COLLAPSED HERE. The pages are concatenated in
 * the order the server sent them. Two payments against one invoice are two
 * events and reach the file as two rows — production holds invoices whose two
 * mirror rows sum to twice the invoice total, and collapsing them would put one
 * plausible number into a spreadsheet instead of two questionable ones.
 *
 * THE SEARCH TERM IS THE EFFECTIVE ONE. The caller hands down the same `params`
 * object that drives the visible list, so the debounced search value is the one
 * that travels. Reading the raw keystrokes would produce a file that disagrees
 * with the table it was exported from for as long as the debounce lasts.
 *
 * NO REQUEST IS BUILT HERE. The two list clients in `adminData/invoiceDashboard`
 * are the only way out; this file adds paging to them and nothing else. It also
 * never touches the summary client: an export is rows, and a KPI computed beside
 * them would be a second engine next to the backend's.
 */
import { useCallback, useMemo, useRef, useState } from "react";

import {
  fetchInvoiceDashboardInvoices,
  fetchInvoiceDashboardPayments,
} from "../../../api/adminData/invoiceDashboard";
import {
  EXPORT_PAGE_SIZE,
  buildInvoiceCsv,
  buildPaymentCsv,
  exportFilename,
} from "./invoiceDashboardExport";

/** What the operator is told when a run could not be completed. */
const FAILURE_MESSAGE = "Export konnte nicht erstellt werden";

/** The server's word for "this source records individual payments". */
const AVAILABLE = "available";

/** The rows of one page, or none — a malformed envelope contributes nothing. */
const rowsOf = (page) => (Array.isArray(page.items) ? page.items : []);

/**
 * Refuse a page that does not answer the question that was asked.
 *
 * The source check is not ceremony: the whole programme rests on never showing
 * Bexio and Vantio rows in one place, and a file is the one artefact where that
 * mistake survives long enough to be forwarded.
 */
function assertPage(page, source, view) {
  if (page == null || typeof page !== "object") {
    throw new Error("Der Server hat eine unerwartete Antwort geliefert.");
  }
  if (page.source !== source) {
    throw new Error(
      `Der Server hat für eine andere Quelle geantwortet ("${page.source}").`
    );
  }
  if (view === "payments" && page.availability !== AVAILABLE) {
    // `not_recorded` is not an empty population. A header-only file would claim
    // the opposite of what the server just said.
    throw new Error("Für diese Quelle werden keine Zahlungen erfasst.");
  }
}

/**
 * Page the endpoint from the beginning until the first answer's `total` is
 * covered, and hand back the rows in the order they arrived.
 */
async function collectAllRows(fetchPage, baseParams, source, view) {
  const rows = [];

  const first = await fetchPage({
    ...baseParams,
    limit: EXPORT_PAGE_SIZE,
    offset: 0,
  });
  assertPage(first, source, view);
  rows.push(...rowsOf(first));

  // Frozen here and never re-read. A `total` that grows on page three belongs
  // to a population that did not exist when this run started.
  const initialTotal = typeof first.total === "number" ? first.total : rows.length;

  let nextOffset = EXPORT_PAGE_SIZE;
  while (nextOffset < initialTotal) {
    const page = await fetchPage({
      ...baseParams,
      limit: EXPORT_PAGE_SIZE,
      offset: nextOffset,
    });
    assertPage(page, source, view);
    const pageRows = rowsOf(page);
    rows.push(...pageRows);
    // A page that answers with nothing while the count says otherwise means the
    // population shrank under us. Continuing would only ask the same question
    // again; stopping leaves a file that is short rather than endless.
    if (pageRows.length === 0) break;
    nextOffset += EXPORT_PAGE_SIZE;
  }

  return rows;
}

/** Hand the finished document to the browser and release the object URL. */
function downloadCsv(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
}

export default function useInvoiceDashboardExport({
  view,
  source,
  params,
  total,
  availability,
} = {}) {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  // Kept so a test — and a future preview — can read the document that was
  // written without intercepting the download.
  const [lastCsv, setLastCsv] = useState("");

  // A ref rather than the `exporting` flag: a second click can land in the same
  // tick as the first, before React has re-rendered with the new state.
  const runningRef = useRef(false);

  const isPayments = view === "payments";

  // The list's parameters MINUS everything that describes a page. Deleted rather
  // than never read, so a caller can keep handing down the object it already
  // has and no third copy of the filter state exists.
  const baseParams = useMemo(() => {
    const next = { ...(params || {}) };
    delete next.limit;
    delete next.offset;
    return next;
  }, [params]);

  const canExport =
    (source === "bexio" || source === "native") &&
    typeof total === "number" &&
    total > 0 &&
    (!isPayments || availability === AVAILABLE);

  const runExport = useCallback(async () => {
    if (runningRef.current) return;
    if (!canExport) return;
    runningRef.current = true;
    setExporting(true);
    setError("");
    try {
      const fetchPage = isPayments
        ? fetchInvoiceDashboardPayments
        : fetchInvoiceDashboardInvoices;
      const rows = await collectAllRows(fetchPage, baseParams, source, view);
      const text = isPayments ? buildPaymentCsv(rows) : buildInvoiceCsv(rows, source);
      const filename = exportFilename({ view, source });
      if (!filename) {
        throw new Error("Für diese Ansicht ist kein Dateiname definiert.");
      }
      setLastCsv(text);
      downloadCsv(text, filename);
    } catch (err) {
      setLastCsv("");
      setError(`${FAILURE_MESSAGE}: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      runningRef.current = false;
      setExporting(false);
    }
  }, [canExport, isPayments, baseParams, source, view]);

  return { runExport, exporting, error, canExport, lastCsv };
}
